import type { Module } from 'vuex';
import { UserTelegramConnection } from '@/types/UserTelegramConnection';
import {RootState} from "@/store";

export interface UserTelegramConnectionState {
  userTelegramConnections: UserTelegramConnection[];
  selectedIdUserTelegramConnection: number | null;
}

const userTelegramConnectionModule: Module<UserTelegramConnectionState, RootState> = {
  namespaced: true,

  state: () => ({
    userTelegramConnections: [],
    selectedIdUserTelegramConnection: null,
  }),

  getters: {
    selectedTelegramConnection(state: UserTelegramConnectionState): UserTelegramConnection | null {
      if (state.selectedIdUserTelegramConnection !== null) {
        return state.userTelegramConnections.find(
          c => c.idUserTelegramConnection === state.selectedIdUserTelegramConnection
        ) ?? null;
      }
      return null;
    },
  },

  mutations: {
    setUserTelegramConnections(state: UserTelegramConnectionState, payload) {
      state.userTelegramConnections = Object.values(payload.userTelegramConnections).map(UserTelegramConnection.fromRaw);
    },

    setUserTelegramConnectionHook(state: UserTelegramConnectionState, payload) {
      const connection = state.userTelegramConnections.find(
        c => c.idUserTelegramConnection === payload.idUserTelegramConnection
      );
      if (!connection) {
        const oldConnection = state.userTelegramConnections.find(
          c => c.token === payload.token
        );
        if (oldConnection) {
          oldConnection.token = null;
        }

        const connection = UserTelegramConnection.fromRaw(payload);
        state.userTelegramConnections.push(connection);
        return;
      }

      Object.assign(connection, payload);

      // 1️⃣ Сначала находим connection по id
      const connection1 = state.userTelegramConnections.find(
        c => c.idUserTelegramConnection === payload.idUserTelegramConnection
      );
      if (connection1) {
        connection1.token = null;
      }

      // 2️⃣ Теперь ищем connection по token
      if (payload.token) {
        const connection2 = state.userTelegramConnections.find(
          c => c.token === payload.token
        );

        if (connection2) {
          connection2.token = null;
        }
      }
    },

    setUserTelegramConnectionStatus(
      state: UserTelegramConnectionState,
      payload: {
        idUserTelegramConnection: number;
        token: string | null;
        active: boolean;
      },
    ) {
      const connection = state.userTelegramConnections.find(
        c => c.idUserTelegramConnection === payload.idUserTelegramConnection
      );
      if (!connection) return;

      Object.assign(connection, payload);
    },

    setUserTelegramConnectionCreate(
      state: UserTelegramConnectionState,
      payload: {
        idUserTelegramConnection: number;
        idUser: number;
        chatId: number | null;
        token: string | null;
        username: string | null;
        firstName: string | null;
        lastName: string | null;
        languageCode: string | null;
        active: boolean;
        deactivatedByUser: boolean;
      },
    ) {
      const raw = { ...payload };
      const connection = UserTelegramConnection.fromRaw(raw);

      state.userTelegramConnections.push(connection);
    },

    setUserTelegramConnectionDelete(
      state: UserTelegramConnectionState,
      payload: {
        idUserTelegramConnection: number;
        newIdUserTelegramConnection: number | null;
        newToken: string | null;
      },
    ) {
      if (
        state.selectedIdUserTelegramConnection !== null &&
        state.selectedIdUserTelegramConnection === payload.idUserTelegramConnection
      ) {
        state.selectedIdUserTelegramConnection = null;
      }

      const idUser = state.userTelegramConnections.find(
        c => c.idUserTelegramConnection === payload.idUserTelegramConnection
      )?.idUser;

      state.userTelegramConnections = state.userTelegramConnections.filter(
        c => c.idUserTelegramConnection !== payload.idUserTelegramConnection
      );

      if (payload.newIdUserTelegramConnection !== null) {
        const raw = {
          idUserTelegramConnection: payload.newIdUserTelegramConnection,
          idUser: idUser,
          chatId: null,
          token: payload.newToken,
          username: null,
          firstName: null,
          lastName: null,
          languageCode: null,
          active: false,
          deactivatedByUser: false,
        }
        const connection = UserTelegramConnection.fromRaw(raw);

        state.userTelegramConnections.push(connection);

      }
    },

    setUserTelegramConnectionDeactivate(
      state: UserTelegramConnectionState,
      payload: {
        idUserTelegramConnection: number;
      },
    ) {
      const connection = state.userTelegramConnections.find(
        c => c.idUserTelegramConnection === payload.idUserTelegramConnection
      );
      if (!connection) return;

      connection.deactivatedByUser = true;
    },

    setUserTelegramConnectionReactivate(
      state: UserTelegramConnectionState,
      payload: {
        idUserTelegramConnection: number;
      },
    ) {
      const connection = state.userTelegramConnections.find(
        c => c.idUserTelegramConnection === payload.idUserTelegramConnection
      );
      if (!connection) return;

      connection.deactivatedByUser = false;
    },

    selectUserTelegramConnection(state: UserTelegramConnectionState, idUserTelegramConnection: number | null) {
      state.selectedIdUserTelegramConnection = idUserTelegramConnection;
    },
  },

  actions: {
    wsSetUserTelegramConnections(context, payload) {
      context.commit('setUserTelegramConnections', payload);
    },
    wsUserTelegramConnectionHook(context, payload) {
      context.commit('setUserTelegramConnectionHook', payload);
    },
    wsUserTelegramConnectionStatus(context, payload) {
      context.commit('setUserTelegramConnectionStatus', payload);
    },
    wsUserTelegramConnectionCreate(context, payload) {
      context.commit('setUserTelegramConnectionCreate', payload);
    },
    wsUserTelegramConnectionDelete(context, payload) {
      context.commit('setUserTelegramConnectionDelete', payload);
    },
    wsUserTelegramConnectionDeactivate(context, payload) {
      context.commit('setUserTelegramConnectionDeactivate', payload);
    },
    wsUserTelegramConnectionReactivate(context, payload) {
      context.commit('setUserTelegramConnectionReactivate', payload);
    },
    selectUserTelegramConnection(context, payload) {
      context.commit('selectUserTelegramConnection', payload);
    },
  },
};

export default userTelegramConnectionModule;
